/**
 * PushTargetRenderer - Tab coordinator for push notification configuration
 *
 * Switches between Quick Config (title/body/action) and Template Config
 * (template selection + variable mapping + preview) modes.
 * Both modes share PushDeliveryConfig for target/sound/badge.
 */

import React from 'react';
import { PushQuickConfig } from './PushQuickConfig';
import { PushTemplateConfig } from './PushTemplateConfig';
import { useI18n } from '@/i18n';
import type { BaseCustomRendererProps } from './types';

export const PushTargetRenderer: React.FC<BaseCustomRendererProps> = (props) => {
  const { nodeData, isDisabled = false, onBatchUpdate } = props;
  const { t } = useI18n();

  const pushMode = (nodeData.push_mode as string) || 'quick';

  const handleModeChange = (mode: 'quick' | 'template') => {
    if (onBatchUpdate) {
      onBatchUpdate({ push_mode: mode });
    }
  };

  return (
    <div className="space-y-4">
      {/* Mode Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          type="button"
          onClick={() => handleModeChange('quick')}
          disabled={isDisabled}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            pushMode === 'quick'
              ? 'border-rose-500 text-rose-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          {t('imageWorkflows.pushTarget.quickConfigTab')}
        </button>
        <button
          type="button"
          onClick={() => handleModeChange('template')}
          disabled={isDisabled}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            pushMode === 'template'
              ? 'border-rose-500 text-rose-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          {t('imageWorkflows.pushTarget.templateConfigTab')}
        </button>
      </div>

      {/* Tab Content */}
      {pushMode === 'quick' ? (
        <PushQuickConfig {...props} />
      ) : (
        <PushTemplateConfig {...props} />
      )}
    </div>
  );
};

export default PushTargetRenderer;
